import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.instarizz.me';
const API_KEY = process.env.REACT_APP_API_KEY || 'IUdh3897euawu2ehuBE78y3nuei2EIOed2hjq89emimNer82iequ2';

// Helper functions to get API URL and KEY
export const getApiUrl = () => API_URL;
export const getApiKey = () => API_KEY;

// Configuration of axios instance with common settings
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json' // Default Content-Type for most requests
  }
});

// ---- NEW Request Interceptor ----
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't set Content-Type here generically if axios.create already does it,
    // or if some requests (e.g. multipart/form-data) override it.
    // The setting in axios.create is usually sufficient as default.
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// ---- End of Interceptor ----

// Function to prepare images for upload
const prepareImagesForUpload = (files) => {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('image', files[i]);
  }
  return formData;
};

// Mock responses for use without API
const getMockAnalysis = (context) => {
  const mockAnalyses = {
    general: "This Instagram profile suggests a sociable person who appreciates culture and outdoor activities. The images show an interest in travel, gastronomy, and leisure moments with friends. The communication appears authentic and relaxed. For an effective approach, show interest in their passions and ask open questions about the places visited or shared experiences.",
    romantic: "The profile reveals a charismatic personality with a strong aesthetic sense. There is a balance between personal and social moments, suggesting a person who values both intimacy and social connections. For a romantic approach, find common interests evident in the photos and use these as conversation starters, maintaining a respectful and authentic tone.",
    professional: "This Instagram profile shows a person with diverse interests and good presentation skills. The images suggest organization and attention to detail. In a professional context, I would value their versatility and communication skills. A collaborative approach, mentioning specific projects where their skills would be valuable, could be effective.",
    friendship: "The profile shows a person with multiple passions and a strong social orientation. The images suggest openness to new experiences and a friendly nature. To build a friendship, focus on shared interests visible in the feed and propose related activities. A direct and genuine approach will likely be appreciated.",
    networking: "This profile suggests a person with various interests and an active social network. For networking, identify shared professional interests and start with a specific comment on relevant content. Maintain a professional but friendly tone, expressing genuine interest in their projects and offering connection opportunities without being invasive."
  };
  
  return {
    analysis: mockAnalyses[context] || "The analysis of this profile is not available. Try again with a different context.",
    success: true
  };
};

// Analysis service
export const analyzeProfile = async (imageFiles, context, specificGoal) => {
  try {
    console.log("Starting profile analysis...");
    
    const formData = prepareImagesForUpload(imageFiles);
    formData.append('context', context);
    formData.append('analysis_style', 'comprehensive');
    formData.append('length', 'max_detail');
    formData.append('language', 'en');
    formData.append('min_sections', '8');
    formData.append('min_traits', '8');
    formData.append('complete_analysis', 'true');
    formData.append('max_tokens', '4000');
    formData.append('required_sections', 'Immediate Impact,MBTI Personality');
    formData.append('section_prompts', 'What is the immediate impact and feeling that the profile conveys to a new visitor in the first few seconds?,Which MBTI personality type is perceived from the photos?');
    
    if (specificGoal && specificGoal.trim()) {
      formData.append('specific_goal', specificGoal);
    }
    
    // The Authorization header is now added by the interceptor.
    // No need to manually add the token here anymore.
    // const token = localStorage.getItem('token'); 
    
    console.log("Sending request to:", `${API_URL}/analyze_image`);
    // console.log("With token:", token ? "Present" : "Missing"); // The token is handled by the interceptor
    console.log("Number of images:", imageFiles.length);

    const response = await api.post(`/analyze_image`, formData, {
      headers: {
        // Content-Type is specific for this request and overrides the default of the api instance
        'Content-Type': 'multipart/form-data' 
        // The Authorization header will be automatically added by the interceptor
      },
      // Increase timeout to allow longer responses
      timeout: 120000
    });
    
    console.log("API Response:", response.data);
    console.log("Response size (bytes):", JSON.stringify(response.data).length);
    
    const analysisText = response.data.result || response.data.analysis || "No analysis result received.";
    console.log("Analysis length (chars):", analysisText.length);
    
    // Ensure response is in the format expected by AnalysisForm
    return {
      analysis: analysisText,
      success: true
    };
    
    // Fallback to mock if needed
    /*
    console.log("Using mock responses for analysis");
    
    // Delay simulation to provide a more realistic experience
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return getMockAnalysis(context);
    */
  } catch (error) {
    console.error("Error during analysis:", error);
    if (error.response) {
      console.error("Error response:", error.response.data);
      console.error("Status code:", error.response.status);
      console.error("Headers:", error.response.headers);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    throw error;
  }
};

// Updated function to save analysis to history using fetch
export const saveAnalysisToHistory = async (analysisData, token) => {
  try {
    console.log("API Service: Calling PUT /api/analysis/history", analysisData); // Log per debug

    const response = await fetch('https://api.instarizz.me/api/analysis/history', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, // Usa il token per autenticare la richiesta
      },
      body: JSON.stringify(analysisData), // Invia i dati dell'analisi come JSON
    });

    const data = await response.json();

    if (!response.ok) {
      // Gestisci errori di risposta HTTP dal backend
      const error = new Error(data.message || `HTTP error! status: ${response.status}`);
      error.response = response; // Allega la risposta completa per debugging
      error.data = data; // Allega i dati JSON della risposta d'errore
      throw error;
    }

    console.log("API Service: PUT /api/analysis/history successful", data); // Log di successo
    return data; // Restituisce la risposta di successo (es. { message: ..., analysis_id: ... })

  } catch (error) {
    console.error("API Service: Error in saveAnalysisToHistory:", error);
    throw error; // Rilancia l'errore per essere gestito dove la funzione viene chiamata
  }
};

export default {
  analyzeProfile,
  saveAnalysisToHistory,
  client: api
}; 